import { CheckCircleIcon } from '@components/Icons/CheckCircleIcon';
import { LoadingSpinnerIcon } from '@components/Icons/LoadingSpinnerIcon';
import { TriangleExclamationMarkIcon } from '@components/Icons/TriangleExclamationMarkIcon';
import { OwnerShortfall } from '@lib/utils/ownerAffordability';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { ImpactData, ImpactPopup } from './ImpactPopup';
import css from './PreUploadStatusMessage.module.scss';

type Status = 'error' | 'valid' | 'warning' | 'loading' | 'duplicate' | 'neutral';

interface PreUploadStatusMessageProps {
  children: ReactNode;
  status: Status;
  info?: ReactNode;
  impactData?: ImpactData;
  className?: string;
  /** Forwarded to `ImpactPopup`: the library's owner is billed, not the current user. */
  billedToAnotherAccount?: boolean;
  /** Forwarded to `ImpactPopup`: the owner's name, when the backend gave us one. */
  ownerName?: string;
  /** Forwarded to `ImpactPopup`: set when the pre-flight probe said the owner is short. */
  shortfall?: OwnerShortfall;
}

const iconMap: Record<Status, typeof CheckCircleIcon | typeof AnnouncementIcon | null> = {
  error: TriangleExclamationMarkIcon,
  valid: CheckCircleIcon,
  warning: TriangleExclamationMarkIcon,
  loading: LoadingSpinnerIcon,
  duplicate: TriangleExclamationMarkIcon,
  neutral: AnnouncementIcon,
};

const statusClassMap: Record<Status, string> = {
  error: css.statusError,
  valid: css.statusValid,
  warning: css.statusWarning,
  loading: css.statusLoading,
  duplicate: css.statusDuplicate,
  neutral: css.statusNeutral,
};

const parseMessage = (message: ReactNode): ReactNode => {
  if (typeof message !== 'string') {
    return message;
  }

  // Check if message matches the pattern: "TypeText • ..."
  // This matches messages like "Video found • 2 new videos • 1:23:45 duration"
  const parts = message.split(' • ');
  if (parts.length > 1) {
    return (
      <>
        <span className={css.typeText}>{parts[0]}</span>
        {' • '}
        {parts.slice(1).join(' • ')}
      </>
    );
  }

  return message;
};

export const PreUploadStatusMessage = ({
  children,
  status,
  info,
  impactData,
  className,
  billedToAnotherAccount = false,
  ownerName,
  shortfall,
}: PreUploadStatusMessageProps) => {
  const IconComponent = iconMap[status];
  const statusClass = statusClassMap[status];
  const isNeutral = status === 'neutral';
  const [showPopup, setShowPopup] = useState(false);

  const hasImpactData = impactData && (impactData.credits || impactData.storage);

  if (isNeutral) {
    return (
      <div className={classNames(css.statusMessage, className)}>
        <span
          className={classNames(css.statusClass, statusClass)}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {IconComponent && (
            <span className={css.statusIcon} aria-hidden="true">
              <IconComponent />
            </span>
          )}
          {children}
        </span>
      </div>
    );
  }

  const parsedMessage = parseMessage(children);

  return (
    <div
      className={classNames(css.statusMessage, css.statusBanner, statusClass, className)}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {IconComponent && (
        <span className={css.statusIcon} aria-hidden="true">
          <IconComponent />
        </span>
      )}
      <span className={css.statusText}>{parsedMessage}</span>
      {info !== undefined || hasImpactData ? (
        <div
          className={css.infoIconWrapper}
          onMouseEnter={() => setShowPopup(true)}
          onMouseLeave={() => setShowPopup(false)}
        >
          <InfoOutlinedIcon className={css.infoIcon} />
          {showPopup && hasImpactData && (
            <ImpactPopup
              data={impactData}
              billedToAnotherAccount={billedToAnotherAccount}
              ownerName={ownerName}
              shortfall={shortfall}
            />
          )}
        </div>
      ) : (
        <InfoOutlinedIcon className={classNames(css.infoIcon, css.infoIconHidden)} />
      )}
    </div>
  );
};
